import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Clock, ArrowLeft, CalendarCheck } from "lucide-react";

export default function Booking() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data)); }, []);

  // Return from Stripe checkout -> poll payment status
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDone(data.booking);
          setVerifying(false);
          toast.success("Payment received — your session is confirmed!");
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          setVerifying(false);
          toast.error("Payment was not completed. Please try booking again.");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setVerifying(false);
        toast.error("Could not verify payment. Please check your email.");
      }
    };
    poll();
  }, [params]);

  useEffect(() => {
    if (user && user !== false) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  useEffect(() => {
    setTime("");
    if (!date || !serviceId) { setSlots([]); return; }
    setLoadingSlots(true);
    api.get("/availability", { params: { date: format(date, "yyyy-MM-dd"), service_id: serviceId } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date, serviceId]);

  const service = services.find((s) => s.id === serviceId);

  const submit = async () => {
    if (!serviceId || !date || !time || !form.name || !form.email) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form, service_id: serviceId, date: format(date, "yyyy-MM-dd"), time,
        origin_url: window.location.origin,
      });
      if (data.requires_payment) {
        toast.info("Redirecting to secure checkout…");
        window.location.href = data.checkout_url;
        return;
      }
      setDone(data.booking);
      toast.success("Your session is confirmed!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Booking failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="bg-cream min-h-screen" data-testid="payment-verifying">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 pt-40 pb-24 text-center">
          <div className="w-16 h-16 border-2 border-forest border-t-transparent rounded-full animate-spin mx-auto mb-8" />
          <h1 className="font-serif font-light text-3xl text-forest">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-3">One moment while we secure your session.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="bg-cream min-h-screen" data-testid="booking-confirmation">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 pt-40 pb-24 text-center">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}
            className="w-20 h-20 rounded-full bg-forest text-cream flex items-center justify-center mx-auto mb-8">
            <CalendarCheck className="w-9 h-9" />
          </motion.div>
          <p className="overline text-clay mb-4">You're all set</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl text-forest mb-6">Session confirmed</h1>
          <div className="bg-white border border-border p-8 text-left max-w-md mx-auto space-y-3">
            <Row label="Service" value={done.service_name} />
            <Row label="Date" value={done.date} />
            <Row label="Time" value={done.time} />
            <Row label="Duration" value={`${done.duration} min`} />
            <Row label="Name" value={done.name} />
          </div>
          <p className="text-muted-foreground mt-6">A confirmation has been sent to <span className="text-forest">{done.email}</span>.</p>
          <div className="flex justify-center gap-4 mt-8">
            <Button onClick={() => navigate("/")} variant="outline" className="rounded-none border-forest text-forest" data-testid="conf-home-btn">Back home</Button>
            {user && user !== false && (
              <Button onClick={() => navigate("/dashboard")} className="rounded-none bg-forest hover:bg-forest-dark text-cream" data-testid="conf-dashboard-btn">View my sessions</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen" data-testid="booking-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-muted-foreground hover:text-forest transition-colors mb-8" data-testid="booking-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <p className="overline text-clay mb-4">Book a session</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-forest mb-12">Reserve your time</h1>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Step 1: service */}
          <div>
            <p className="overline text-forest mb-5">01 · Select a service</p>
            <div className="space-y-px bg-border mb-12">
              {services.map((s) => (
                <button key={s.id} onClick={() => setServiceId(s.id)}
                  className={`w-full text-left p-5 flex items-center justify-between transition-colors duration-300 ${serviceId === s.id ? "bg-forest text-cream" : "bg-white hover:bg-surfacealt text-forest"}`}
                  data-testid={`select-service-${s.id}`}>
                  <div>
                    <p className="font-serif text-xl">{s.name}</p>
                    <p className={`text-sm flex items-center gap-1.5 mt-1 ${serviceId === s.id ? "text-cream/70" : "text-muted-foreground"}`}>
                      <Clock className="w-3.5 h-3.5" /> {s.duration} min
                    </p>
                  </div>
                  <span className="font-serif text-xl">{s.price === 0 ? "Free" : `$${s.price}`}</span>
                </button>
              ))}
            </div>

            <p className="overline text-forest mb-5">02 · Pick a date</p>
            <div className="bg-white border border-border p-4 inline-block">
              <Calendar mode="single" selected={date} onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                data-testid="booking-calendar" />
            </div>
          </div>

          {/* Step 2: time + details */}
          <div>
            <p className="overline text-forest mb-5">03 · Choose a time</p>
            {!date || !serviceId ? (
              <p className="text-muted-foreground mb-12" data-testid="slots-hint">Select a service and date to see available times.</p>
            ) : loadingSlots ? (
              <p className="text-muted-foreground mb-12">Loading times…</p>
            ) : (
              <div className="grid grid-cols-3 gap-3 mb-12" data-testid="time-slots">
                {slots.map((s) => (
                  <button key={s.time} disabled={!s.available} onClick={() => setTime(s.time)}
                    className={`py-3 border text-sm transition-colors duration-300 ${
                      time === s.time ? "bg-forest text-cream border-forest"
                      : s.available ? "bg-white border-border text-forest hover:border-forest"
                      : "bg-surfacealt border-border text-muted-foreground/40 line-through cursor-not-allowed"}`}
                    data-testid={`slot-${s.time}`}>
                    {s.time}
                  </button>
                ))}
              </div>
            )}

            <p className="overline text-forest mb-5">04 · Your details</p>
            <div className="space-y-4">
              <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none bg-white border-border focus-visible:ring-forest h-12" data-testid="booking-name" />
              <Input type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none bg-white border-border focus-visible:ring-forest h-12" data-testid="booking-email" />
              <Textarea placeholder="Anything you'd like me to know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-none bg-white border-border focus-visible:ring-forest" data-testid="booking-notes" />
            </div>

            <div className="mt-8 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {service && time && date && (
                  <span data-testid="booking-summary">{service.name} · {format(date, "MMM d")} · {time}</span>
                )}
              </div>
              <Button onClick={submit} disabled={submitting}
                className="rounded-none bg-forest hover:bg-forest-dark text-cream px-8 py-6 transition-colors duration-300" data-testid="booking-submit-btn">
                {submitting ? "Processing…" : service && service.price > 0
                  ? (<><Check className="w-4 h-4 mr-2" /> Pay ${service.price} &amp; confirm</>)
                  : (<><Check className="w-4 h-4 mr-2" /> Confirm booking</>)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-border pb-2 last:border-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="text-forest font-medium">{value}</span>
    </div>
  );
}
